import { useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { Button, Textarea, Spinner, MessageBar, MessageBarBody } from "@fluentui/react-components";
import { Send24Regular, CheckmarkCircle24Regular } from "@fluentui/react-icons";

import {
    useAppDispatch,
    useAppSelector,
    selectFeedbackMessage,
    selectFeedbackIsSubmitting,
    selectFeedbackIsSubmitted,
    selectFeedbackError,
    selectAppFeedbackEnabled,
    setFeedbackMessage,
    submitFeedbackForm,
    resetFeedback
} from "../../store";
import { getSessionId } from "../../services/sessionService";
import styles from "./Feedback.module.css";

export const Feedback = () => {
    const dispatch = useAppDispatch();
    const appFeedbackEnabled = useAppSelector(selectAppFeedbackEnabled);

    // All hooks must be called before any conditional returns (rules-of-hooks)
    const sessionId = useMemo(() => getSessionId(), []);
    const message = useAppSelector(selectFeedbackMessage);
    const isSubmitting = useAppSelector(selectFeedbackIsSubmitting);
    const isSubmitted = useAppSelector(selectFeedbackIsSubmitted);
    const error = useAppSelector(selectFeedbackError);

    const handleTextChange = useCallback(
        (_: unknown, data: { value: string }) => {
            dispatch(setFeedbackMessage(data.value));
        },
        [dispatch]
    );

    const handleSubmit = useCallback(() => {
        if (!message.trim()) return;

        void dispatch(
            submitFeedbackForm({
                message: message.trim(),
                sessionId
            })
        );
    }, [dispatch, message, sessionId]);

    const handleReset = useCallback(() => {
        dispatch(resetFeedback());
    }, [dispatch]);

    // Redirect to home if feedback is disabled
    if (!appFeedbackEnabled) {
        return <Navigate to="/" replace />;
    }

    const canSubmit = message.trim().length > 0 && !isSubmitting;

    return (
        <main className={styles.pages}>
            <h1>Feedback</h1>
            <p className={styles.description}>
                We value your feedback! Please share your thoughts, suggestions, or report any issues you've encountered.
            </p>

            {error && (
                <MessageBar intent="error" role="alert" className={styles.errorMessage}>
                    <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
            )}

            {isSubmitted ? (
                <div className={styles.successContainer} role="status" aria-live="polite">
                    <CheckmarkCircle24Regular className={styles.successIcon} aria-hidden="true" />
                    <h2 className={styles.successTitle}>Thank you for your feedback!</h2>
                    <p className={styles.successMessage}>Your comments have been submitted successfully.</p>
                    <Button appearance="primary" onClick={handleReset}>
                        Submit More Feedback
                    </Button>
                </div>
            ) : (
                <>
                    <section className={styles.formSection}>
                        <label htmlFor="feedback-text" className={styles.label}>
                            Your Comments
                        </label>
                        <Textarea
                            id="feedback-text"
                            className={styles.textarea}
                            placeholder="Enter your feedback here..."
                            value={message}
                            onChange={handleTextChange}
                            disabled={isSubmitting}
                            resize="vertical"
                            aria-describedby="feedback-hint"
                        />
                        <p id="feedback-hint" className={styles.srOnly}>
                            Share your thoughts, suggestions, or report any issues you've encountered.
                        </p>
                    </section>

                    <div className={styles.actions}>
                        <Button
                            appearance="primary"
                            icon={isSubmitting ? <Spinner size="tiny" aria-hidden="true" /> : <Send24Regular aria-hidden="true" />}
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            aria-busy={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit Feedback"}
                        </Button>
                    </div>
                </>
            )}
        </main>
    );
};

export default Feedback;
